// ===================================
// CHATGPT-INSPIRED PUBLIC CHAT PAGE - src/pages/PublicChatPage.tsx
// NOW WITH DYNAMIC BUSINESS ID FROM URL
// ===================================
import type React from "react";
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom"; // 👈 ADDED: useLocation
import PublicChat from "../components/PublicChat";
import { publicChatApi, agenticAuthApi, sessionManager } from "../services/api";
import AiChatIcon from "../assets/aiChatIcon.png";

import {
  MessageSquare,
  Shield,
  Zap,
  User,
  LogIn,
  Key,
  Lock,
  CheckCircle,
  AlertTriangle,
  Menu,
  X,
} from "lucide-react";

const PublicChatPage: React.FC = () => {
  // 👇 NEW: Extract business ID from URL query params (supports both 'businessId' and 'id')
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  // Support both 'businessId' and 'id' query params for flexibility
  const businessId =
    queryParams.get("businessId") || queryParams.get("id") || "52"; // Default to 52 if not provided

  console.log("🔍 [PublicChatPage] Business ID from URL:", businessId);

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [lastAgenticNotice, setLastAgenticNotice] = useState<string | null>(
    null,
  );

  // Agentic authentication state
  const [agenticAuthState, setAgenticAuthState] = useState({
    showModal: false,
    loading: false,
    error: "",
    code: "manifest-123456", // Default code (will be business-specific)
    lastAttempt: null as Date | null,
    isVerified: false,
  });

  // Track current session for auth purposes
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  const quickActions = [
    {
      icon: "📊",
      title: "Analyze Data",
      prompt: "Can you analyze my recent business data and provide insights?",
    },
    {
      icon: "📄",
      title: "Search Documents",
      prompt:
        "Help me find information in my documents about quarterly reports",
    },
    {
      icon: "✍️",
      title: "Create Report",
      prompt:
        "Create a comprehensive report summarizing this month's performance",
    },
    {
      icon: "💡",
      title: "Get Suggestions",
      prompt:
        "What are some recommendations to improve my business operations?",
    },
  ];

  // Check initial verification status
  useEffect(() => {
    const isVerified = sessionManager.isAgenticVerified();
    setAgenticAuthState((prev) => ({ ...prev, isVerified }));
  }, []);

  // Listen for chat events and agentic notices
  useEffect(() => {
    const handleAgenticNotice = (event: any) => {
      if (event.detail?.intent || event.detail?.fallback) {
        const intentInfo = event.detail.intent;

        if (intentInfo) {
          setLastAgenticNotice(`${intentInfo.action} (${intentInfo.topic})`);
        }

        setShowLoginPrompt(true);
        setAgenticAuthState((prev) => ({
          ...prev,
          showModal: true,
          error: "",
          code: "manifest-123456", // Reset to default
          pendingSessionId: event.detail.sessionId || currentSessionId,
          pendingMessageId: event.detail.messageId,
        }));
      }
    };

    const handleSessionCreated = (event: any) => {
      if (event.detail?.sessionId) {
        setCurrentSessionId(event.detail.sessionId);
        console.log(
          "🔍 [PublicChatPage] Session created:",
          event.detail.sessionId,
        );
      }
    };

    const handleNotification = (event: any) => {
      const { type, title, message } = event.detail;
      console.log(`${type.toUpperCase()}: ${title} - ${message}`);
    };

    window.addEventListener("agenticNotice", handleAgenticNotice);
    window.addEventListener("publicChatSessionCreated", handleSessionCreated);
    window.addEventListener("showNotification", handleNotification);

    return () => {
      window.removeEventListener("agenticNotice", handleAgenticNotice);
      window.removeEventListener(
        "publicChatSessionCreated",
        handleSessionCreated,
      );
      window.removeEventListener("showNotification", handleNotification);
    };
  }, [currentSessionId]);

  // Handle agentic verification
  const handleAgenticVerification = async () => {
    if (!agenticAuthState.code.trim()) {
      setAgenticAuthState((prev) => ({
        ...prev,
        error: "Please enter your access code",
      }));
      return;
    }

    console.log(
      "🔐 [PublicChatPage] Attempting verification with code:",
      agenticAuthState.code.substring(0, 3) + "***",
    );

    setAgenticAuthState((prev) => ({
      ...prev,
      loading: true,
      error: "",
      lastAttempt: new Date(),
    }));

    try {
      if (currentSessionId) {
        console.log(
          "🔐 [PublicChatPage] Authenticating session:",
          currentSessionId,
        );

        const response = await publicChatApi.authenticateSession(
          currentSessionId,
          {
            accessCode: agenticAuthState.code.trim(),
          },
        );

        if (response.data.success) {
          sessionManager.setAgenticVerified(response.data.principalId);

          setAgenticAuthState({
            showModal: false,
            loading: false,
            error: "",
            code: "manifest-123456",
            lastAttempt: null,
            isVerified: true,
          });

          setShowLoginPrompt(false);
          setLastAgenticNotice(null);

          const successEvent = new CustomEvent("showNotification", {
            detail: {
              type: "success",
              title: "Authentication Successful",
              message: "Access granted. Your action has been processed.",
            },
          });
          window.dispatchEvent(successEvent);

          if (response.data.replay_response) {
            setTimeout(() => {
              const replayEvent = new CustomEvent("agenticActionReplayed", {
                detail: { response: response.data.replay_response },
              });
              window.dispatchEvent(replayEvent);

              const executedEvent = new CustomEvent("showNotification", {
                detail: {
                  type: "success",
                  title: "Action Executed",
                  message: "Your request has been processed automatically.",
                },
              });
              window.dispatchEvent(executedEvent);
            }, 100);
          }
        } else {
          setAgenticAuthState((prev) => ({
            ...prev,
            loading: false,
            error:
              response.data.message ||
              "Invalid access code. Please try again by calling the previous action and then enter a valid verification code.",
          }));
        }
      } else {
        console.log("🔐 [PublicChatPage] No session ID, using legacy auth");

        const response = await agenticAuthApi.verify({
          accessKey: agenticAuthState.code.trim(),
        });

        if (response.data.ok) {
          sessionManager.setAgenticVerified(response.data.principalId);

          setAgenticAuthState({
            showModal: false,
            loading: false,
            error: "",
            code: "manifest-123456",
            lastAttempt: null,
            isVerified: true,
          });

          setShowLoginPrompt(false);
          setLastAgenticNotice(null);

          if (response.data.replay) {
            setTimeout(() => {
              const replayEvent = new CustomEvent("agenticActionReplayed", {
                detail: { response: response.data.replay },
              });
              window.dispatchEvent(replayEvent);
            }, 100);
          }
        } else {
          setAgenticAuthState((prev) => ({
            ...prev,
            loading: false,
            error:
              "Invalid access code. Please try again by calling the previous action and then enter a valid verification code.",
          }));
        }
      }
    } catch (error: any) {
      console.error("❌ [PublicChatPage] Verification error:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Verification failed. Please try again.";

      setAgenticAuthState((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0f1e] via-[#1a1f3a] to-[#0a0f1e] flex flex-col">
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0f1e]/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo/Brand */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-white font-semibold text-lg hidden sm:block">
                MicroAgent Chatbot
              </span>
              {agenticAuthState.isVerified && (
                <span className="hidden md:flex items-center text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded-full">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verified
                </span>
              )}
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <Link
                to="/login"
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-all"
              >
                <LogIn className="w-4 h-4" />
                <span>Staff Login</span>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg"
            >
              {showMobileMenu ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden bg-[#0a0f1e] border-t border-white/10">
            <div className="px-4 py-3 space-y-2">
              <Link
                to="/login"
                className="flex items-center space-x-2 px-4 py-3 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-all"
                onClick={() => setShowMobileMenu(false)}
              >
                <LogIn className="w-4 h-4" />
                <span>Staff Login</span>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Main Chat Container */}
      <div className="flex-1 flex flex-col items-center justify-center pt-14 pb-4 px-4">
        <div className="w-full max-w-4xl mx-auto flex flex-col h-full">
          {/* Welcome Header - Only show when no messages */}

          {/* Chat Interface */}
          <div className="flex-1 w-full max-w-3xl mx-auto">
            {/* 👇 CHANGED: Pass dynamic businessId instead of hardcoded "52" */}
            <PublicChat
              isEmbedded={true}
              className="h-full rounded-xl border border-white/10 shadow-2xl"
              businessId={businessId}
            />
          </div>

          {/* Footer Info */}
          <div className="text-center mt-6 space-y-2">
            <div className="flex items-center justify-center space-x-6 text-xs text-gray-500">
              <span className="flex items-center space-x-1">
                <Shield className="w-3 h-3" />
                <span>Secure</span>
              </span>
              <span className="flex items-center space-x-1">
                <Zap className="w-3 h-3" />
                <span>AI-Powered</span>
              </span>
              {agenticAuthState.isVerified && (
                <span className="flex items-center space-x-1 text-green-400">
                  <Lock className="w-3 h-3" />
                  <span>Enhanced</span>
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600">
              Powered by Pridict AI • Need more help?{" "}
              <a
                href="mailto:support@pridict.com"
                className="text-purple-400 hover:text-purple-300"
              >
                Contact us
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Enhanced Login Prompt Toast */}
      {showLoginPrompt && (
        <div className="fixed top-20 right-4 max-w-sm bg-gradient-to-r from-yellow-900/90 to-orange-900/90 backdrop-blur-lg border border-yellow-500/50 rounded-xl p-4 shadow-2xl z-50 animate-slide-in">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-white mb-1">
                Authentication Required
              </h4>
              <p className="text-xs text-gray-300 mb-3">
                {lastAgenticNotice
                  ? `Action "${lastAgenticNotice}" requires authentication.`
                  : "This action requires special permissions."}
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() =>
                    setAgenticAuthState((prev) => ({
                      ...prev,
                      showModal: true,
                    }))
                  }
                  className="text-xs px-3 py-1.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center space-x-1"
                >
                  <Key className="w-3 h-3" />
                  <span>Enter Code</span>
                </button>
                <button
                  onClick={() => setShowLoginPrompt(false)}
                  className="text-xs px-3 py-1.5 text-gray-300 hover:text-white"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agentic Authentication Modal */}
      {agenticAuthState.showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50 p-4">
          <div className="bg-gradient-to-br from-[#1a1f3a] to-[#0a0f1e] rounded-2xl shadow-2xl p-6 w-full max-w-md border border-white/10">
            <div className="flex items-start space-x-3 mb-4">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Key className="w-5 h-5 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-white">
                  Authentication Required
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  {lastAgenticNotice
                    ? `To perform "${lastAgenticNotice}", please enter your access code.`
                    : "This action requires special permissions. Please enter your access code to continue."}
                </p>
              </div>
            </div>

            {agenticAuthState.error && (
              <div className="mb-4 p-3 bg-red-900/50 border border-red-500/50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <p className="text-sm text-red-300">
                    {agenticAuthState.error}
                  </p>
                </div>
              </div>
            )}

            <div className="mb-4">
              <label
                htmlFor="accessCode"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Access Code
              </label>
              <input
                id="accessCode"
                type="text"
                value={agenticAuthState.code}
                onChange={(e) =>
                  setAgenticAuthState((prev) => ({
                    ...prev,
                    code: e.target.value,
                    error: "",
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !agenticAuthState.loading) {
                    handleAgenticVerification();
                  }
                  if (e.key === "Escape") {
                    setAgenticAuthState((prev) => ({
                      ...prev,
                      showModal: false,
                    }));
                  }
                }}
                className="w-full bg-[#0a0f1e] border border-white/10 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white placeholder-gray-500"
                placeholder="Enter your access code"
                disabled={agenticAuthState.loading}
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2">
                This code grants enhanced access for 24 hours.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() =>
                  setAgenticAuthState((prev) => ({
                    ...prev,
                    showModal: false,
                    code: "manifest-123456",
                    error: "",
                  }))
                }
                disabled={agenticAuthState.loading}
                className="px-4 py-2 text-gray-300 hover:text-white disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAgenticVerification}
                disabled={
                  agenticAuthState.loading || !agenticAuthState.code.trim()
                }
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 flex items-center space-x-2 transition-all"
              >
                {agenticAuthState.loading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <Key className="w-4 h-4" />
                <span>
                  {agenticAuthState.loading ? "Verifying..." : "Verify Access"}
                </span>
              </button>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10 text-center">
              <p className="text-xs text-gray-500">
                Having trouble?{" "}
                <a
                  href="mailto:support@pridict.com"
                  className="text-purple-400 hover:text-purple-300"
                >
                  Contact support
                </a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add animation keyframes to your global CSS or inline style */}
      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PublicChatPage;
